import { useRef, useState } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { fileUrl, uploadFile, errMsg } from "@/lib/api";

export const PageHeader = ({ title, subtitle, actions, testid }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6" data-testid={testid}>
    <div>
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{title}</h1>
      {subtitle && <p className="text-slate-500 mt-1 text-sm sm:text-base">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Pill = ({ map, value, testid }) => {
  const m = map[value] || { label: value, cls: "bg-slate-100 text-slate-700" };
  return <span data-testid={testid} className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold whitespace-nowrap ${m.cls}`}>{m.label}</span>;
};

export const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-[13px] font-semibold text-slate-600">{label}</Label>
    {children}
  </div>
);

export const Card = ({ children, className = "", ...rest }) => (
  <div className={`bg-white border border-border rounded-2xl ${className}`} {...rest}>{children}</div>
);

export const Empty = ({ text, testid }) => <p className="text-slate-500 text-sm py-8 text-center" data-testid={testid}>{text}</p>;

const fmt = (n) => (n === "" || n === null || n === undefined ? "" : Number(n).toLocaleString("id-ID"));

export const MoneyInput = ({ value, onChange, testid, className = "", ...rest }) => (
  <div className="relative">
    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-semibold">Rp</span>
    <Input inputMode="numeric" data-testid={testid} className={`pl-10 h-12 text-base ${className}`} value={fmt(value)} {...rest}
      onChange={(e) => {
        const digits = e.target.value.replace(/[^\d]/g, "");
        onChange(digits === "" ? "" : Number(digits));
      }} />
  </div>
);

export const ImageUpload = ({ value, onChange, testid, isPublic = true, compact = false }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      onChange(await uploadFile(f, isPublic));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-3">
      {value && isPublic && <img src={fileUrl(value)} alt="" className={`${compact ? "w-14 h-14" : "w-24 h-20"} rounded-xl object-cover border border-border`} />}
      {value && !isPublic && <span className="text-xs text-emerald-700 font-semibold">Bukti terlampir</span>}
      <button type="button" onClick={() => ref.current.click()} data-testid={testid}
        className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-border bg-white text-sm font-semibold hover:bg-sand">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
        {value ? "Change" : "Upload"}
      </button>
      <input ref={ref} type="file" accept="image/*,application/pdf" className="hidden" onChange={pick} />
    </div>
  );
};

export const BigButton = ({ children, className = "", ...rest }) => (
  <button className={`inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl font-bold text-[15px] bg-ocean text-white hover:bg-oceandeep active:scale-[0.98] transition-[background-color,transform] duration-150 disabled:opacity-50 ${className}`} {...rest}>{children}</button>
);

export const GhostButton = ({ children, className = "", ...rest }) => (
  <button className={`inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl font-semibold text-[15px] bg-white border border-border hover:bg-sand transition-colors duration-150 disabled:opacity-50 ${className}`} {...rest}>{children}</button>
);
